import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";
import { h, resolveComponent, watch } from "vue";
import useUser from "@/composables/useUser";
import useOidc from "@/composables/useOidc";
import useGlobalLoading from "@/composables/useGlobalLoading";

const routes: Array<RouteRecordRaw> = [
  {
    path: "/me",
    name: "me",
    component: () => import("@/views/Me.vue"),
  },
  {
    path: "/unauthorized",
    name: "unauthorized",
    component: () => import("@/views/NotAuthorized.vue"),
    meta: {
      isPublic: true,
    },
  },
  {
    path: "/organizations",
    component: {
      render: () => h(resolveComponent("router-view")),
    },
    meta: {
      breadcrumb: "Organisationer",
    },
    children: [
      {
        path: "",
        name: "organizations",
        component: () => import("@/views/Organizations.vue"),
        meta: {
          breadcrumb: "Organisationer",
        },
      },
      {
        path: ":organizationId",
        props: true,
        name: "organization",
        component: () => import("@/views/Organization.vue"),
        meta: {
          breadcrumb: (route) =>
            route.query.title?.toString() ?? "Vis organisation",
        },
        children: [
          {
            path: "users",
            meta: {
              breadcrumb: "Profilsøgning",
            },
            redirect: (to) => {
              return "/organizations/" + to.params.organizationId.toString();
            },
          },
          {
            path: "users/:id",
            name: "user",
            meta: {
              needsParentInHistory: true,
            },
            props: true,
            component: () => import("@/views/User/User.vue"),
          },
        ],
      },
    ],
  },
  {
    path: "/applications",
    meta: {
      breadcrumb: "Applikationer",
      requiresAuth: true,
    },
    component: {
      render: () => h(resolveComponent("router-view")),
    },
    children: [
      {
        path: "",
        name: "applications",
        component: () => import("@/views/Applications.vue"),
      },
      {
        path: ":applicationId",
        name: "application",
        meta: {
          breadcrumb: (route) =>
            route.query.title?.toString() ?? "Vis applikation",
          requiresAuth: true,
        },
        props: true,
        component: () => import("@/views/Application.vue"),
        children: [
          {
            path: "roles/:id",
            name: "role",
            props: true,
            component: () => import("@/views/Role/Role.vue"),
          },
          {
            path: "profiles/:id",
            name: "profile",
            props: true,
            component: () => import("@/views/Profile/Profile.vue"),
          },
        ],
      },
    ],
  },
  {
    path: "/logs",
    component: {
      render: () => h(resolveComponent("router-view")),
    },
    meta: {
      breadcrumb: "Logs",
      requiresAuth: true,
    },
    children: [
      {
        path: "",
        name: "logs",
        component: () => import("@/views/Logs.vue"),
        meta: {
          breadcrumb: "Logs",
        },
      },
      {
        path: ":id",
        name: "log",
        props: true,
        component: () => import("@/views/Log/Log.vue"),
        meta: {
          breadcrumb: "Log Detaljer",
        },
      },
    ],
  },
  {
    path: "/users",
    component: {
      render: () => h(resolveComponent("router-view")),
    },
    meta: {
      breadcrumb: "Profilsøgning",
    },
    children: [
      {
        path: "",
        name: "users",
        component: () => import("@/views/Users.vue"),
        meta: {
          breadcrumb: "User",
        },
      },
    ],
  },
  {
    path: "/setting",
    meta: {
      breadcrumb: "Indstillinger",
      requiresAuth: true,
    },
    component: {
      render: () => h(resolveComponent("router-view")),
    },
    children: [
      {
        path: "",
        name: "setting",
        component: () => import("@/views/Setting/Setting.vue"),
        meta: {
          breadcrumb: "Indstillinger",
        },
      },
      {
        path: "/setting/batch-role-assignment",
        name: "batchRoleAssignment",
        component: () => import("@/views/BatchRoleAssignment.vue"),
        meta: {
          breadcrumb: (route) =>
            route.query.title?.toString() ?? "Batch rolletildeling",
          requiresAuth: true,
        },
      },
      {
        path: "/setting/forbindelsesoversigt",
        name: "forbindelsesoversigt",
        component: () => import("@/views/Forbindelsesoversigt.vue"),
        meta: {
          breadcrumb: (route) =>
            route.query.title?.toString() ?? "Forbindelsesoversigt",
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: "/claims-overview",
    name: "ClaimsOverview",
    component: () => import("@/views/ClaimsOverview.vue"),
    meta: {},
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: function (to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, top: 0 };
    }
  },
});

const user = useUser();

router.beforeEach(async (to, from, next) => {
  // When oidc has been initialized, and is logged in, we want to ensure that the user is downloaded from the graph before continuing.
  if (useOidc().user.value != null) {
    const promise = new Promise<void>((resolve) => {
      useGlobalLoading().startLoading();
      if (user.user.value == null) {
        watch(
          () => user.user.value,
          (newValue) => {
            if (newValue) resolve();
          },
          { immediate: true }
        );
      } else {
        resolve();
      }
    });

    await promise;
    next();
    useGlobalLoading().stopLoading();
  } else {
    next();
  }
});

router.beforeEach(async (to, from, next) => {
  if (useOidc().user.value == null) {
    // If OIDC is not logged in, we allow all paths, as we do not want to disturb login flow
    next();
  } else if (
    to.meta.isPublic ||
    user.user.value?.isSuperAdmin ||
    // user admin and application admin can access orgs, applications, users and logs
    ((user.user.value?.isUserAdmin || user.user.value?.isApplicationAdmin) &&
      (to.path.includes("organizations") ||
        to.path.includes("applications") ||
        to.path.includes("users") ||
        to.path.includes("logs"))) ||
    // admin can access organizations
    (user.user?.value?.isAdmin &&
      to.params.organizationId == user.user.value?.organizationId) ||
    to.path == "/me" ||
    to.path == "/logs" ||
    to.path == "/" ||
    to.path == "/claims-overview"
  ) {
    // Handle role-based redirection
    if (to.path === "/") {
      if (user.user.value?.isSuperAdmin) {
        return next("/organizations");
      } else if (user.user.value?.isAdmin) {
        return next("/organizations/" + user.user.value.organizationId);
      } else if (user.user.value?.isUserAdmin) {
        return next("/users");
      } else if (user.user.value?.isApplicationAdmin) {
        return next("/applications");
      } else {
        next();
      }
    }

    if (from.path == "/" && to.meta.needsParentInHistory) {
      next(false);
      await router.push({
        name: to.matched[to.matched.length - 2].name,
        params: to.params,
      });
      await router.push({ name: to.name ?? undefined, params: to.params });
    } else if (to.query) {
      next();
    }
  } else {
    // User is not authorized for that path
    next({
      path: "/unauthorized",
    });
  }
});

export default router;
